import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  TextInput,
  Select,
  CheckBox,
  Button,
  Heading,
  Layer,
  Tag,
} from "grommet";
import adminAxios from "../../config/adminAxios";

const AddNewPortfolioModal = ({ showModal, setShowModal, onPortfolioCreated }) => {
  // Form state
  const [portfolioName, setPortfolioName] = useState("");
  const [selectedLearningPaths, setSelectedLearningPaths] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [importAllCoursesForSelectedLearningPaths, setImportAllCoursesForSelectedLearningPaths] = useState(false);

  // Dropdown options
  const [learningPathOptions, setLearningPathOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);

  // UI state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation
  const isFormValid =
    portfolioName.trim();

  // Fetch data from API
  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true);
      try {
        const response = await adminAxios.get("/api/admin/fetchdetails");
        const data = response.data;
        const skillNameById = (data.skills || []).reduce((acc, skill) => {
          acc[Number(skill.skill_id)] = skill.skill_name;
          return acc;
        }, {});

        // Transform data for dropdowns
        if (data.learning_paths) {
          setLearningPathOptions(
            data.learning_paths.map((lp) => ({
              label: lp.learning_path_name,
              value: lp.learning_path_id,
              course_ids: lp.course_ids || [],
            }))
          );
        }

        if (data.skills) {
          setSkillOptions(
            data.skills.map((skill) => ({
              label: skill.skill_name,
              value: skill.skill_id,
            }))
          );
        }

        if (data.courses) {
          setCourseOptions(
            data.courses.map((course) => ({
              label: `${course.course_name} (${course.skill_name || skillNameById[Number(course.skill_id)] || "Unknown Skill"})`,
              value: course.id,
              course_name: course.course_name,
              skill_id: course.skill_id,
              skill_name: course.skill_name || skillNameById[Number(course.skill_id)] || "Unknown Skill",
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, []);

  const selectedSkillIds = useMemo(
    () => selectedSkills.map((skill) => Number(skill.value)),
    [selectedSkills]
  );

  const filteredCourseOptions = useMemo(
    () =>
      selectedSkillIds.length > 0
        ? courseOptions.filter((course) =>
            selectedSkillIds.includes(Number(course.skill_id))
          )
        : [],
    [courseOptions, selectedSkillIds]
  );

  const courseById = useMemo(
    () =>
      courseOptions.reduce((acc, course) => {
        acc[Number(course.value)] = course;
        return acc;
      }, {}),
    [courseOptions]
  );

  const selectedSkillsDisplay = useMemo(
    () => selectedSkills.map((skill) => skill.label),
    [selectedSkills]
  );

  const selectedLearningPathsDisplay = useMemo(
    () => selectedLearningPaths.map((lp) => lp.label),
    [selectedLearningPaths]
  );

  const coursesViaSkillsRows = useMemo(
    () =>
      selectedCourses.map((course) => {
        const fullCourse = courseById[Number(course.value)] || course;
        return {
          source: "Via Skills",
          learningPathName: "-",
          skillName: fullCourse.skill_name || "Unknown Skill",
          courseName: fullCourse.course_name || fullCourse.label || `Course ${fullCourse.value}`,
        };
      }),
    [selectedCourses, courseById]
  );

  const coursesViaLearningPathsRows = useMemo(() => {
    if (!importAllCoursesForSelectedLearningPaths) return [];
    return selectedLearningPaths.flatMap((lp) =>
      ((lp.course_ids || learningPathOptions.find((opt) => opt.value === lp.value)?.course_ids) || []).map((courseId) => {
        const course = courseById[Number(courseId)];
        return {
          source: "Via Learning Paths",
          learningPathName: lp.label,
          skillName: course?.skill_name || "Unknown Skill",
          courseName: course?.course_name || `Course ${courseId}`,
        };
      })
    );
  }, [importAllCoursesForSelectedLearningPaths, selectedLearningPaths, learningPathOptions, courseById]);

  const confirmationCourseRows = useMemo(
    () => [...coursesViaSkillsRows, ...coursesViaLearningPathsRows],
    [coursesViaSkillsRows, coursesViaLearningPathsRows]
  );
  const hasScrollableLearningPaths = selectedLearningPathsDisplay.length > 3;
  const hasScrollableSkills = selectedSkillsDisplay.length > 3;

  useEffect(() => {
    if (!selectedSkills.length && selectedCourses.length) {
      setSelectedCourses([]);
      return;
    }

    if (selectedSkills.length && selectedCourses.length) {
      const allowedCourseIds = new Set(filteredCourseOptions.map((course) => course.value));
      setSelectedCourses((prev) =>
        prev.every((course) => allowedCourseIds.has(course.value))
          ? prev
          : prev.filter((course) => allowedCourseIds.has(course.value))
      );
    }
  }, [selectedSkills, selectedCourses, filteredCourseOptions]);

  // Handle removing selected items
  const handleRemoveLearningPath = (valueToRemove) => {
    setSelectedLearningPaths(
      selectedLearningPaths.filter((lp) => lp.value !== valueToRemove)
    );
  };

  const handleRemoveCourse = (valueToRemove) => {
    setSelectedCourses(
      selectedCourses.filter((course) => course.value !== valueToRemove)
    );
  };

  const handleRemoveSkill = (valueToRemove) => {
    setSelectedSkills(
      selectedSkills.filter((skill) => skill.value !== valueToRemove)
    );
  };

  // Show confirmation modal
  const handleReviewClick = () => {
    if (!isFormValid) return;
    setShowConfirmationModal(true);
  };

  // Final submission
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        portfolio_name: portfolioName,
        learning_path_ids: selectedLearningPaths.map((lp) => lp.value),
        course_ids: selectedCourses.map((course) => course.value),
        skill_ids: selectedSkills.map((skill) => skill.value),
        import_all_courses_for_selected_learning_paths: importAllCoursesForSelectedLearningPaths,
      };

      console.log(payload);
      const response = await adminAxios.post("/api/admin/createnewportfolio", payload);

      setShowConfirmationModal(false);
      setShowModal(false);
      if (typeof onPortfolioCreated === "function") {
        onPortfolioCreated(response?.data?.message || "Portfolio created successfully");
      }
    } catch (err) {
      console.error("Error adding portfolio:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!showModal) return null;

  
  if (showConfirmationModal) {
    return (
      <Layer
        onEsc={() => !isSubmitting && setShowConfirmationModal(false)}
        onClickOutside={() => !isSubmitting && setShowConfirmationModal(false)}
        responsive={false}
      >
        <Box pad="medium" gap="small" width="xlarge" background="white" round="small">
          <Heading level={3} margin="none">
            Confirm Portfolio Details
          </Heading>

          <Box direction="row" gap="small" align="center">
            <Text weight="bold" size="small">
              Portfolio Name:
            </Text>
            <Text size="small">{portfolioName}</Text>
          </Box>

          <Box
            pad="xsmall"
            background="light-1"
            round="xsmall"
          >
            <Box direction="row" justify="between" align="center">
              <Text weight="bold" size="small">Learning Paths ({selectedLearningPathsDisplay.length})</Text>
              {hasScrollableLearningPaths && (
                <Text size="xsmall" color="dark-5">
                  More learning paths available. Scroll to view all.
                </Text>
              )}
            </Box>
            <Box
              margin={{ top: "xsmall" }}
              gap="xxsmall"
              height={hasScrollableLearningPaths ? "72px" : "auto"}
              overflow={hasScrollableLearningPaths ? { vertical: "auto" } : undefined}
            >
              {selectedLearningPathsDisplay.map((learningPathName, index) => (
                <Text key={`${learningPathName}-${index}`} size="small">
                  {index + 1}. {learningPathName}
                </Text>
              ))}
            </Box>
          </Box>

          <Box
            direction="row"
            gap="xsmall"
            align="center"
            pad="xsmall"
            background="light-1"
            round="xsmall"
          >
            <Text weight="bold" size="small">Import All Courses From LP:</Text>
            <Box direction="row" gap="xsmall">
              <Button
                label="Yes"
                size="small"
                primary={importAllCoursesForSelectedLearningPaths}
                secondary={!importAllCoursesForSelectedLearningPaths}
                onClick={() => setImportAllCoursesForSelectedLearningPaths(true)}
                disabled={isSubmitting}
              />
              <Button
                label="No"
                size="small"
                primary={!importAllCoursesForSelectedLearningPaths}
                secondary={importAllCoursesForSelectedLearningPaths}
                onClick={() => setImportAllCoursesForSelectedLearningPaths(false)}
                disabled={isSubmitting}
              />
            </Box>
          </Box>

          <Box
            pad="xsmall"
            background="light-1"
            round="xsmall"
          >
            <Box direction="row" justify="between" align="center">
              <Text weight="bold" size="small">Skills ({selectedSkillsDisplay.length})</Text>
              {hasScrollableSkills && (
                <Text size="xsmall" color="dark-5">
                  More skills available. Scroll to view all.
                </Text>
              )}
            </Box>
            <Box
              margin={{ top: "xsmall" }}
              gap="xxsmall"
              height={hasScrollableSkills ? "72px" : "auto"}
              overflow={hasScrollableSkills ? { vertical: "auto" } : undefined}
            >
              {selectedSkillsDisplay.map((skillName, index) => (
                <Text key={`${skillName}-${index}`} size="small">
                  {index + 1}. {skillName}
                </Text>
              ))}
            </Box>
          </Box>

          <Box
            pad="xsmall"
            background="light-1"
            round="xsmall"
          >
            <Text weight="bold" size="small">
              Courses ({confirmationCourseRows.length})
            </Text>
            <Box margin={{ top: "xsmall" }} height="220px" overflow={{ vertical: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", borderBottom: "1px solid #d9d9d9", padding: "6px" }}>Source</th>
                    <th style={{ textAlign: "left", borderBottom: "1px solid #d9d9d9", padding: "6px" }}>LP Name</th>
                    <th style={{ textAlign: "left", borderBottom: "1px solid #d9d9d9", padding: "6px" }}>Skill Name</th>
                    <th style={{ textAlign: "left", borderBottom: "1px solid #d9d9d9", padding: "6px" }}>Course</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmationCourseRows.length > 0 ? (
                    confirmationCourseRows.map((row, index) => (
                      <tr key={`${row.source}-${row.learningPathName}-${row.courseName}-${index}`}>
                        <td style={{ borderBottom: "1px solid #efefef", padding: "6px" }}>{row.source}</td>
                        <td style={{ borderBottom: "1px solid #efefef", padding: "6px" }}>{row.learningPathName}</td>
                        <td style={{ borderBottom: "1px solid #efefef", padding: "6px" }}>{row.skillName}</td>
                        <td style={{ borderBottom: "1px solid #efefef", padding: "6px" }}>{row.courseName}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        style={{ padding: "8px", color: "#666" }}
                      >
                        No courses selected.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>

          <Box direction="row" gap="xsmall" justify="end" margin={{ top: "xsmall" }}>
            <Button
              label="Back"
              onClick={() => setShowConfirmationModal(false)}
              disabled={isSubmitting}
            />
            <Button
              label="Create Portfolio"
              primary
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
            />
          </Box>
        </Box>
      </Layer>
    );
  }
  return (
    <Layer
      onEsc={() => setShowModal(false)}
      onClickOutside={() => setShowModal(false)}
      responsive={false}
    >
      <Box pad="medium" gap="small" width="large">
        <Heading level={2} margin="none">
          Add New Portfolio
        </Heading>

        <Text size="small" color="status-warning">
          Please fill in all the required fields.
        </Text>

        {/* Portfolio Name */}
        <Box>
          <Text>
            Portfolio Name <Text color="red">*</Text>
          </Text>
          <TextInput
            placeholder="Enter portfolio name"
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
          />
        </Box>

          {/* Learning Paths */}
          <Box>
            <Box direction="row" align="center" justify="between">
              <Text>Learning Paths</Text>
              <Box direction="row" align="center" gap="xxsmall">
                <CheckBox
                  checked={importAllCoursesForSelectedLearningPaths}
                  onChange={(event) =>
                    setImportAllCoursesForSelectedLearningPaths(event.target.checked)
                  }
                  style={{ transform: "scale(0.85)" }}
                />
                <Text size="small">Import all courses for selected learning paths</Text>
              </Box>
            </Box>
          <Select
            multiple
            placeholder={isLoading ? "Loading..." : "Select learning paths (multiple)"}
            options={learningPathOptions}
            labelKey="label"
            valueKey="value"
            value={selectedLearningPaths}
            onChange={({ value }) => setSelectedLearningPaths(value)}
            disabled={isLoading}
            dropHeight="185px"
          />
          {selectedLearningPaths.length > 0 && (
            <Box
              direction="row"
              gap="xsmall"
              margin={{ top: "small" }}
              pad={{ vertical: "xxsmall", horizontal: "small" }}
              background="light-2"
              round="xsmall"
              overflow={{ horizontal: "auto", vertical: "hidden" }}
              style={{ flexWrap: "nowrap" }}
            >
              {selectedLearningPaths.map((lp) => (
                <Tag
                  key={lp.value}
                  value={lp.label}
                  onRemove={() => handleRemoveLearningPath(lp.value)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Skills */}
        <Box>
          <Text>
            Skills 
          </Text>
          <Select
            multiple
            placeholder={isLoading ? "Loading..." : "Select skills (multiple)"}
            options={skillOptions}
            labelKey="label"
            valueKey="value"
            value={selectedSkills}
            onChange={({ value }) => setSelectedSkills(value)}
            disabled={isLoading}
            dropHeight="185px"
          />
          {selectedSkills.length > 0 && (
            <Box
              direction="row"
              gap="xsmall"
              margin={{ top: "small" }}
              pad={{ vertical: "xxsmall", horizontal: "small" }}
              background="light-2"
              round="xsmall"
              overflow={{ horizontal: "auto", vertical: "hidden" }}
              style={{ flexWrap: "nowrap" }}
            >
              {selectedSkills.map((skill) => (
                <Tag
                  key={skill.value}
                  value={skill.label}
                  onRemove={() => handleRemoveSkill(skill.value)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Courses */}
        <Box>
          <Box direction="row" align="center" justify="between" margin={{ bottom: "xsmall" }}>
            <Text>Courses</Text>
            <Text size="small" color="dark-4">
              Only selected skills courses are shown.
            </Text>
          </Box>
          <Select
            multiple
            placeholder={
              isLoading
                ? "Loading..."
                : selectedSkills.length
                  ? "Select courses (multiple)"
                  : "Select skills first to view courses"
            }
            options={filteredCourseOptions}
            labelKey="label"
            valueKey="value"
            value={selectedCourses}
            onChange={({ value }) => setSelectedCourses(value)}
            disabled={isLoading || selectedSkills.length === 0}
            dropHeight="185px"
          />
          {selectedCourses.length > 0 && (
            <Box
             direction="row"
              gap="xsmall"
              margin={{ top: "small" }}
              pad={{ vertical: "xxsmall", horizontal: "small" }}
              background="light-2"
              round="xsmall"
              overflow={{ horizontal: "auto", vertical: "hidden" }}
              style={{ flexWrap: "nowrap" }}
            >
              {selectedCourses.map((course) => (
                <Tag
                  key={course.value}
                  value={course.label}
                  onRemove={() => handleRemoveCourse(course.value)}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box direction="row" justify="end" gap="small" margin={{ top: "medium" }}>
          <Button label="Cancel" onClick={() => setShowModal(false)} />
          <Button
            label="Review & Confirm"
            primary
            onClick={handleReviewClick}
            disabled={!isFormValid || isLoading}
          />
        </Box>
      </Box>
    </Layer>
  );
};

export default AddNewPortfolioModal;
